import { useRef, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { colors } from "@/theme/colors";
import { createCycle } from "@/api/cycles";
import { ApiError } from "@/api/client";

/**
 * Upload flow — drop one or many PDFs → name each → go (Feedback V2 rev02):
 *  r2-2  select multiple files and process them in sequence;
 *  r2-6  per-file upload progress bar;
 *  r2-10 explicit success confirmation;
 *  r2-11 name suggestions drawn from the last few uploads.
 *
 * Files are processed sequentially (one createCycle at a time) so a batch of
 * large ciclos doesn't hammer the backend / signed-upload path at once.
 */

const RECENT_NAMES_KEY = "marcador.recentUploadNames";
const RECENT_LIMIT = 3;

type ItemStatus = "pending" | "uploading" | "processing" | "done" | "error";

type UploadItem = {
  id: string;
  file: File;
  name: string;
  status: ItemStatus;
  progress: number; // 0..1, upload fraction
  cycleId?: string;
  error?: string;
};

function readRecentNames(): string[] {
  if (typeof localStorage === "undefined") return [];
  try {
    const raw = localStorage.getItem(RECENT_NAMES_KEY);
    const arr = raw ? (JSON.parse(raw) as unknown) : [];
    return Array.isArray(arr)
      ? arr.filter((x): x is string => typeof x === "string")
      : [];
  } catch {
    return [];
  }
}

function rememberName(name: string) {
  if (typeof localStorage === "undefined") return;
  const next = [name, ...readRecentNames().filter((n) => n !== name)].slice(
    0,
    RECENT_LIMIT,
  );
  localStorage.setItem(RECENT_NAMES_KEY, JSON.stringify(next));
}

/** Strip a trailing extension so the default cycle name is the bare filename. */
function defaultNameFor(file: File): string {
  return file.name.replace(/\.[^.]+$/, "");
}

export function CyclesNew() {
  const [items, setItems] = useState<UploadItem[]>([]);
  const [pageFilter, setPageFilter] = useState("");
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [recent] = useState<string[]>(() => readRecentNames());
  const idSeq = useRef(0);

  function addFiles(fileList: FileList | null) {
    if (!fileList || fileList.length === 0) return;
    const added: UploadItem[] = Array.from(fileList).map((file) => ({
      id: `f${idSeq.current++}`,
      file,
      name: defaultNameFor(file),
      status: "pending",
      progress: 0,
    }));
    setItems((prev) => [...prev, ...added]);
    setDone(false);
    setFormError(null);
  }

  function patchItem(id: string, patch: Partial<UploadItem>) {
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...patch } : it)));
  }

  function removeItem(id: string) {
    setItems((prev) => prev.filter((it) => it.id !== id));
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (busy) return;
    const queue = items.filter((it) => it.status !== "done");
    if (queue.length === 0) return;
    if (queue.some((it) => !it.name.trim())) {
      setFormError("Dê um nome a cada arquivo antes de enviar.");
      return;
    }
    setBusy(true);
    setFormError(null);
    setDone(false);

    for (const it of queue) {
      patchItem(it.id, { status: "uploading", progress: 0, error: undefined });
      try {
        const { cycleId } = await createCycle(it.file, it.name.trim(), {
          pageFilter: pageFilter.trim() || undefined,
          onProgress: (fraction) => patchItem(it.id, { progress: fraction }),
        });
        // Bytes are up; the backend is now staging/enqueuing the extract job.
        patchItem(it.id, { status: "done", progress: 1, cycleId });
        rememberName(it.name.trim());
      } catch (err) {
        let msg = err instanceof ApiError ? err.message : String(err);
        if (err instanceof ApiError) {
          const body = err.body as { detail?: string } | undefined;
          if (body?.detail) msg = body.detail;
        }
        patchItem(it.id, { status: "error", error: msg });
      }
    }

    setBusy(false);
    setDone(true);
  }

  const successCount = items.filter((it) => it.status === "done").length;
  const errorCount = items.filter((it) => it.status === "error").length;
  const pendingCount = items.filter((it) => it.status !== "done").length;

  return (
    <form
      onSubmit={onSubmit}
      style={{
        maxWidth: 620,
        display: "flex",
        flexDirection: "column",
        gap: "var(--space-base)",
      }}
    >
      <h2 style={{ fontSize: "var(--fs-display)", lineHeight: "var(--lh-display)" }}>
        Novo PDF
      </h2>

      <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <span style={{ fontSize: "var(--fs-ui)", color: colors.textMuted }}>
          Arquivos PDF (pode selecionar vários)
        </span>
        <input
          type="file"
          accept="application/pdf"
          multiple
          onChange={(e) => {
            addFiles(e.target.files);
            e.target.value = ""; // allow re-selecting the same file
          }}
        />
      </label>

      {recent.length > 0 && (
        <datalist id="recent-cycle-names">
          {recent.map((n) => (
            <option key={n} value={n} />
          ))}
        </datalist>
      )}

      {items.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {items.map((it) => (
            <UploadRow
              key={it.id}
              item={it}
              busy={busy}
              onRename={(name) => patchItem(it.id, { name })}
              onRemove={() => removeItem(it.id)}
            />
          ))}
        </div>
      )}

      <details
        style={{
          border: `1px solid ${colors.line}`,
          borderRadius: "var(--radius-sm)",
          background: colors.panel,
          padding: "8px 12px",
        }}
      >
        <summary
          style={{
            cursor: "pointer",
            fontSize: "var(--fs-ui)",
            color: colors.textMuted,
            fontWeight: 500,
            userSelect: "none",
          }}
        >
          Opções avançadas
        </summary>
        <div style={{ display: "flex", flexDirection: "column", gap: 4, paddingTop: 10 }}>
          <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <span style={{ fontSize: "var(--fs-ui)", color: colors.textMuted }}>
              Páginas a considerar (aplica-se a todos os arquivos)
            </span>
            <input
              value={pageFilter}
              onChange={(e) => setPageFilter(e.target.value)}
              placeholder="todas as páginas (ex: 1-5, 7, 10-12)"
              style={{ ...inputStyle, fontFamily: "var(--font-mono)" }}
            />
            <span style={{ fontSize: "var(--fs-ui)", color: colors.textMuted }}>
              Use vírgulas para separar e <code>-</code> para intervalos. Páginas
              fora desta lista não aparecem na revisão.
            </span>
          </label>
        </div>
      </details>

      {formError && <div style={{ color: colors.danger }}>{formError}</div>}

      {done && successCount > 0 && (
        <div
          role="status"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "10px 12px",
            borderRadius: "var(--radius-sm)",
            background: "rgba(47,107,61,0.10)",
            border: `1px solid ${colors.primary}`,
            color: colors.text,
            fontSize: "var(--fs-body)",
          }}
        >
          <span aria-hidden>✓</span>
          {successCount === 1
            ? "Ciclo criado com sucesso."
            : `${successCount} ciclos criados com sucesso.`}
          {errorCount > 0 && ` ${errorCount} com erro.`}
          <Link to="/cycles" style={{ marginLeft: "auto", color: colors.primary }}>
            Ver ciclos →
          </Link>
        </div>
      )}

      <button type="submit" disabled={busy || pendingCount === 0} style={primaryBtn}>
        {busy ? "Enviando…" : pendingCount > 1 ? `Enviar ${pendingCount} arquivos` : "Enviar"}
      </button>
    </form>
  );
}

function UploadRow({
  item,
  busy,
  onRename,
  onRemove,
}: {
  item: UploadItem;
  busy: boolean;
  onRename: (name: string) => void;
  onRemove: () => void;
}) {
  const pct = Math.round(item.progress * 100);
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 6,
        padding: "10px 12px",
        border: `1px solid ${colors.line}`,
        borderRadius: "var(--radius-sm)",
        background: colors.panel,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <input
          value={item.name}
          list="recent-cycle-names"
          onChange={(e) => onRename(e.target.value)}
          disabled={busy || item.status === "done"}
          placeholder="Nome do ciclo"
          style={{ ...inputStyle, flex: 1 }}
        />
        {item.status === "done" && item.cycleId ? (
          <Link
            to={`/cycles/${item.cycleId}/review`}
            style={{
              color: colors.primary,
              fontSize: "var(--fs-ui)",
              whiteSpace: "nowrap",
            }}
          >
            Revisar →
          </Link>
        ) : (
          !busy && (
            <button
              type="button"
              onClick={onRemove}
              title="Remover"
              aria-label="Remover arquivo"
              style={removeBtn}
            >
              ✕
            </button>
          )
        )}
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          fontSize: "var(--fs-ui)",
          color: colors.textMuted,
        }}
      >
        <span style={{ flex: "none", fontFamily: "var(--font-mono)" }}>
          {(item.file.size / (1024 * 1024)).toFixed(1)} MB
        </span>
        <StatusLabel item={item} pct={pct} />
      </div>
      {item.status === "uploading" && (
        <div
          style={{
            height: 6,
            borderRadius: 3,
            background: colors.panelMuted,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              height: "100%",
              width: `${pct}%`,
              background: colors.primary,
              transition: "width 0.15s linear",
            }}
          />
        </div>
      )}
    </div>
  );
}

function StatusLabel({ item, pct }: { item: UploadItem; pct: number }) {
  switch (item.status) {
    case "uploading":
      return <span>Enviando… {pct}%</span>;
    case "processing":
      return <span>Processando…</span>;
    case "done":
      return <span style={{ color: colors.primary }}>✓ Enviado</span>;
    case "error":
      return <span style={{ color: colors.danger }}>Erro: {item.error}</span>;
    default:
      return <span>Pronto para enviar</span>;
  }
}

const inputStyle: React.CSSProperties = {
  padding: "8px 10px",
  background: colors.panel,
  border: `1px solid ${colors.line}`,
  borderRadius: "var(--radius-sm)",
  fontFamily: "var(--font-body)",
  fontSize: "var(--fs-body)",
  color: colors.text,
};

const removeBtn: React.CSSProperties = {
  flex: "none",
  width: 28,
  height: 28,
  border: `1px solid ${colors.line}`,
  borderRadius: "var(--radius-sm)",
  background: colors.shell,
  color: colors.textMuted,
  cursor: "pointer",
  fontSize: 13,
  lineHeight: 1,
};

const primaryBtn: React.CSSProperties = {
  background: colors.primary,
  color: colors.onPrimary,
  border: "none",
  borderRadius: "var(--radius-sm)",
  padding: "10px 16px",
  fontSize: "var(--fs-body)",
  fontWeight: 600,
  cursor: "pointer",
  alignSelf: "flex-start",
};
